// @ts-check
import clsx from 'clsx';
import {Component} from 'react';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** @param {unknown} date */
function formatDate(date) {
    return date instanceof Date
        ? `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
        : null;
}

const ICONS = {
    area: require('../../icons/datatype/area.png'),
    folder: require('../../icons/datatype/folder.png'),
    map: require('../../icons/datatype/map.png'),
    mapsource: require('../../icons/datatype/map.png'),
    route: require('../../icons/datatype/route.png'),
    track: require('../../icons/datatype/track.png'),
    waypoint: require('../../icons/datatype/waypoint.png'),
};

/**
 * @typedef FilesProps
 * @prop {string | null} position
 * @prop {string | null} displayed
 * @prop {Record<string, any>} objects
 * @prop {Set<number>} selection
 * @prop {any} own
 * @prop {string} mode
 * @prop {(isChecked: boolean) => void} [onToggleAll]
 * @prop {(e: import('react').MouseEvent<HTMLButtonElement>) => void} [onFileMenu]
 * @prop {boolean} async
 * @prop {string} search
 * @prop {any} limit
 * @prop {any} [onEdit]
 * @prop {(id: string, value: string) => void} [onToggle]
 * @prop {(id: string) => void} [onDown]
 * @prop {(id: string) => void} [onChoose]
 * @prop {(id: string, name: string) => void} [onSaveName]
 * @prop {any} [onCancelName]
 *
 * @param {FilesProps} props
 */
export function Files(props) {
    if (props.position === null) {
        return null;
    }

    let allSelected = false;
    let someSelected = false;

    const metadata = props.displayed ? props.objects[props.displayed] : {};
    const /** @type {number[]} */ children = metadata.children ? metadata.children : []; // [0, 1, 2, 3]

    const empty = !children.length;
    const selection = children.map((id) => props.selection.has(id));

    allSelected = !empty && selection.every((selected) => selected);
    someSelected = !selection.every((selected) => !selected);

    const classes = clsx({
        files: true,
        empty,
        parent: props.position === 'parent',
        child: props.position === 'child',
    });

    return (
        <div className={classes}>
            <table>
                <thead>
                    <tr>
                        {props.own && props.mode !== 'choose' && (
                            <th className='check'>
                                <input
                                    type='checkbox'
                                    checked={allSelected}
                                    onChange={(e) => props.onToggleAll?.(e.target.checked)}
                                />
                            </th>
                        )}
                        <th>Title</th>
                        <th>Date Created</th>
                        {props.own ? (
                            <th className='action'>
                                {someSelected ? (
                                    <button
                                        aria-label='Actions'
                                        className='ga-button-secondary'
                                        onClick={(e) => props.onFileMenu?.(e)}
                                    >
                                        <i className='material-icons' aria-hidden='true'>
                                            arrow_drop_down
                                        </i>
                                    </button>
                                ) : null}
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {empty && !props.async ? (
                        <tr>
                            <td colSpan={4}>{"You don't have anything in this folder"}</td>
                        </tr>
                    ) : (
                        children
                            .map((id) => ({
                                id,
                                name: props.objects[id].name,
                                type: props.objects[id].type,
                                date: props.objects[id].created,
                            }))
                            .filter(
                                (f) =>
                                    f.name.toLowerCase().includes(props.search.toLowerCase()) &&
                                    (!props.limit || (props.limit && f.type === props.limit))
                            )
                            .sort((f1, f2) => {
                                // sort by type (e.g. folder, track) then name
                                const order = f1.type.localeCompare(f2.type.localeCompare);
                                return order === 0 ? f1.name.localeCompare(f2.name) : order;
                            })
                            .map((f) => (
                                <File
                                    key={f.id}
                                    id={f.id}
                                    title={f.name}
                                    mode={props.mode}
                                    metadata={props.objects[f.id]}
                                    selected={props.selection.has(f.id)}
                                    own={props.own}
                                    onEdit={props.onEdit}
                                    onToggle={props.onToggle}
                                    onDown={props.onDown}
                                    onFileMenu={props.onFileMenu}
                                    onChoose={props.onChoose}
                                    onSaveName={props.onSaveName}
                                    onCancelName={props.onCancelName}
                                />
                            ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

/**
 * @typedef FileProps
 * @prop {string} title
 * @prop {{
 *  editing: boolean;
 *  type: 'area' | 'folder' | 'map' | 'route' | 'track' | 'waypoint';
 * }} metadata
 */
export class File extends Component {
    /** @param {FileProps} props */
    constructor(props) {
        super(props);

        this.state = {
            originalTitle: props.title,
        };
    }

    /** @param {FileProps} newProps */
    UNSAFE_componentWillReceiveProps(newProps) {
        if (newProps.metadata.editing && !this.props.metadata.editing) {
            this.setState({
                originalTitle: this.props.title,
            });
        }
    }

    /** @param {import("react").KeyboardEvent<HTMLInputElement>} e */
    onKeyDown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            // @ts-ignore
            this.props.onSaveName(this.props.id, e.target.value);
        } else if (e.key === 'Escape') {
            e.preventDefault();

            if (this.props.title !== this.state.originalTitle) {
                this.props.onEdit(this.props.id, this.state.originalTitle);
            }

            this.props.onCancelName();
        }
    }

    render() {
        return (
            // Issue with formatDate(this.props.metadata.created) when displaying the <moveDIalog>
            <tr>
                {this.props.own && this.props.mode !== 'choose' && (
                    <td className='check'>
                        <input
                            type='checkbox'
                            checked={this.props.selected}
                            onChange={(e) => this.props.onToggle(this.props.id, e.target.checked)}
                        />
                    </td>
                )}
                <td>
                    {this.props.metadata.editing ? (
                        <span>
                            <img
                                // @ts-ignore
                                src={ICONS[this.props.metadata.type]}
                                alt={this.props.metadata.type}
                            />
                            <input
                                type='text'
                                value={this.props.title}
                                onKeyDown={(e) => this.onKeyDown(e)}
                                onChange={(e) => this.props.onEdit(this.props.id, e.target.value)}
                            />
                        </span>
                    ) : (
                        <button
                            className='ga-nonbutton linkStyle'
                            onClick={() => this.props.onDown(this.props.id)}
                        >
                            <img
                                // @ts-ignore
                                src={ICONS[this.props.metadata.type]}
                                alt={this.props.metadata.type}
                            />
                            {this.props.title}
                        </button>
                    )}
                </td>
                <td>{formatDate(this.props.metadata.created)}</td>
                {this.props.own ? (
                    <td className='action'>
                        {this.props.mode !== 'choose' && (
                            <button
                                className='ga-button-secondary'
                                onClick={(e) => this.props.onFileMenu(e, this.props.id)}
                            >
                                <i
                                    className='material-icons'
                                    role='img'
                                    aria-label='Actions'
                                    style={{cursor: 'pointer'}}
                                >
                                    arrow_drop_down
                                </i>
                            </button>
                        )}
                        {this.props.mode === 'choose' && (
                            <button
                                className='ga-button-secondary'
                                style={{cursor: 'pointer'}}
                                onClick={() => this.props.onChoose(this.props.id)}
                            >
                                Select
                            </button>
                        )}
                    </td>
                ) : null}
            </tr>
        );
    }
}
